import { useState, useSyncExternalStore } from "react";

import { api } from "~/api/client";
import type { Collision } from "~/api/types";
import { onePress, latest, Writes } from "./writes";

import s from "./Spellings.module.css";

const WHEN = new Intl.DateTimeFormat("en-GB", {
  day: "numeric",
  month: "short",
  year: "2-digit",
  timeZone: "Europe/London",
});

/**
 * The rows two spellings of one name were folded onto, and the two answers to them.
 *
 * `slugify` folds accents on its way to an artist key, so `Adela` and `ADÉLA` are one slug and
 * the second listing to arrive lands on the first act's row: a Slovak singer-songwriter and a
 * DJ with one page, one follow and one list of fifteen dates between them. That folding is
 * right far more often than it is wrong — `Beyoncé` and `Beyonce` are one person — so the rule
 * stays, and what was missing was any way to *find* the rows it had folded. A person had to
 * notice this one on the page.
 *
 * Two answers, because there are two things the row can be and no rule can tell them apart:
 * the difference is who was on the stage.
 *
 * **One act.** Two promoters spelled a name two ways. Recorded so it is never offered again,
 * and nothing about the row changes.
 *
 * **Two acts.** The nights billed under one spelling move onto a new row of their own.
 *
 * A list rather than the one-at-a-time card beside it. Each row is answered from its nights —
 * a club night at Basing House against two nights at Brixton — and there are few enough of
 * them that scrolling is cheaper than pressing through.
 */
export function Spellings({
  rows,
  writes,
}: {
  rows: Collision[];
  /**
   * The page's queue, when there is a page. `Tidy` passes its own, so a spelling answer goes
   * in line with the card's verdicts and survives this section being mounted afresh.
   */
  writes?: Writes;
}) {
  const [own] = useState(() => new Writes());
  const queue = writes ?? own;
  const seen = useSyncExternalStore(queue.subscribe, queue.read);
  /**
   * The rows still to answer: every row whose latest answer is on its way or accepted leaves
   * the list at the press, and one the server refused comes back where it was.
   *
   * Read off the queue rather than refetched: the answer is recorded on the server and the
   * next `/suspects` will not carry the row, but re-asking costs the whole sweep and a query
   * per remaining row to redraw a list the editor is part-way down.
   */
  const left = rows.filter((row) => {
    const said = latest(seen, "spelling", row.artist_id);
    return !said || said.state === "refused" || said.state === "cancelled";
  });
  if (rows.length === 0) return null;

  // Nothing waits on another answer: the pressed row has left the list, so a second press on a
  // row already being split has nothing to land on, and the queue sends one write at a time.
  const answer = (artist: number, verdict: "one" | "two", spelling?: string) =>
    queue.press("spelling", artist, () => api.spelling(artist, verdict, spelling));

  return (
    <section className={s.wrap}>
      <h2 className={s.heading}>One name, two spellings</h2>
      {left.length === 0 ? (
        <p className={s.meta}>All answered.</p>
      ) : (
        <ul className={s.rows}>
          {left.map((row) => (
            <li key={row.artist_id} className={s.row}>
              <p className={s.act}>
                {/* The act's own page, because half of these are settled by looking at it —
                    a photograph, a Last.fm biography and a genre are all evidence about
                    which of the two acts the row thinks it is. */}
                <a
                  href={`/a/${row.artist_id}`}
                  className={s.link}
                  target="_blank"
                  rel="noreferrer"
                >
                  {row.name}
                </a>
                <span className={s.slug}>{row.slug}</span>
              </p>
              {row.spellings.map((spelt) => (
                <div key={spelt.billed_as} className={s.spelt}>
                  <p className={s.forms}>
                    {/* Every form, not just the commonest: `ADÉLA` and `Adéla` are one
                        spelling shouted and said, and a row that showed one of them would be
                        hiding evidence from the person being asked to judge. */}
                    <b className={s.exact}>{spelt.forms.join(" · ")}</b>
                    {/*
                     * The spelling is in the button's own text, not only in the line above
                     * it. Two of these are drawn per row and they read identically without
                     * it — so nothing but screen position said which spelling a press moved,
                     * on a control that moves nights with no undo, and nothing at all said
                     * it to anybody using the page by its accessible names.
                     *
                     * The commonest form, because that is both what the press sends and the
                     * name the new row is given.
                     */}
                    <button
                      type="button"
                      className={s.take}
                      {...onePress(() =>
                        answer(row.artist_id, "two", spelt.billed_as),
                      )}
                    >
                      Two acts — move{" "}
                      <span className={s.exact}>“{spelt.billed_as}”</span> off (
                      {spelt.nights.length} {spelt.nights.length === 1 ? "night" : "nights"})
                    </button>
                  </p>
                  <ul className={s.nights}>
                    {/* Keyed on the position, not the booking. An act billed twice on one
                        bill under one spelling is two entries here — `Spelt.nights` says so —
                        and a duplicate key stops React reconciling the list. */}
                    {spelt.nights.map((night, at) => (
                      <li key={`${at}-${night.event_id}`}>
                        <span className={s.day}>
                          {WHEN.format(new Date(night.starts_at))}
                        </span>
                        <span className={s.venue}>{night.venue}</span>
                        {/* A new tab, both here and on the act above. Answering a row
                            means opening its evidence, and a same-tab link would throw away
                            the place in a list somebody is working down. */}
                        <a
                          href={`/e/${night.event_id}`}
                          className={s.title}
                          target="_blank"
                          rel="noreferrer"
                        >
                          {night.title}
                        </a>
                      </li>
                    ))}
                  </ul>
                </div>
              ))}
              <button
                type="button"
                className={s.keep}
                {...onePress(() => answer(row.artist_id, "one"))}
              >
                One act, spelled two ways
              </button>
              {/* Said rather than swallowed, and on the row it is about: a split can be
                  refused — a spelling that resolves back to this row would move every booking
                  onto itself — and a press that reported nothing would look like one that had
                  worked. */}
              {((said) =>
                said?.state === "refused" ? (
                  <p className={s.trouble}>{said.said}</p>
                ) : null)(latest(seen, "spelling", row.artist_id))}
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}
